import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload } from '@fortawesome/free-solid-svg-icons'; // Add faDownload icon
import { jsPDF } from 'jspdf';

const ProfilingCards = ({ profiles, onViewProfile }) => {

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'twitter': return '#000000';
      case 'facebook': return '#3b5998';
      case 'instagram': return 'linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)';
      case 'tiktok': return 'linear-gradient(45deg, #69C9D0, #9300C7)';
      case 'linkedin': return '#0077B5';
      default: return '#707070';
    }
  };

  const platformIcons = [
    { name: 'facebook', icon: '/icons/facebook.png' },
    { name: 'instagram', icon: '/icons/instagram.png' },
    { name: 'twitter', icon: '/icons/twitter.png' },
    { name: 'tiktok', icon: '/icons/tiktok.png' },
    { name: 'linkedin', icon: '/icons/linkedin.png' }
  ];

  const getPlatformIcon = (platform) => {
    const platformData = platformIcons.find(item => item.name === platform.toLowerCase());
    return platformData ? platformData.icon : '/icons/default.png'; // Default icon if not found
  };
const getProfileImage = async (imgSrc) => {
  try {
    const response = await fetch(`/api/fetch-image/?imgSrc=${encodeURIComponent(imgSrc)}`);
    if (response.ok) {
      const imageBlob = await response.blob();
      return URL.createObjectURL(imageBlob);
    } else {
      console.error("Failed to fetch image from proxy server");
      return null;
    }
  } catch (err) {
    console.error("Error fetching proxied image:", err);
    return null;
  }
};



const generatePDF = async (profile) => {
  const doc = new jsPDF();
  let y = 20;

  // Sanitize and normalize text
  const clean = (value) => {
    if (value === undefined || value === null) return "N/A";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value)
      .replace(/[^\x00-\x7F]/g, "") // Remove non-ASCII
      .replace(/\s+/g, " ")         // Normalize spaces
      .trim() || "N/A";
  };

  const drawSectionTitle = (title) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(title, 14, y);
    y += 10;
  };

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Profile Overview", doc.internal.pageSize.width / 2, y, { align: "center" });
  y += 12;

  // Load profile image
  try {
    const response = await fetch(`/api/fetch-image/?imgSrc=${encodeURIComponent(profile.imgSrc)}`);
    const blob = await response.blob();
    const reader = new FileReader();

    const imageLoaded = new Promise((resolve) => {
      reader.onloadend = () => resolve(reader.result);
    });

    reader.readAsDataURL(blob);
    const imageDataUrl = await imageLoaded;

    const imgWidth = 40;
    const imgHeight = 40;
    const imgX = (doc.internal.pageSize.width - imgWidth) / 2;
    doc.addImage(imageDataUrl, "JPEG", imgX, y, imgWidth, imgHeight);
    y += imgHeight + 10;
  } catch (err) {
    console.warn("Image load failed:", err);
    y += 10;
  }

  // Profile Info
  const info = [
    ["Username", clean(profile.username)],
    ["Full Name", clean(profile.full_name)],
    ["Email", clean(profile.email)],
    ["Location", clean(profile.location)],
    ["Platform", clean(profile.platform)],
    ["Verified", profile.verified ? "Yes" : "No"],
    ["Created Time", clean(profile.created_time)],
    ["Description", clean(profile.description)],
    ["Total Posts", clean(profile.stats?.posts)],
    ["Followers", clean(profile.stats?.followers)],
    ["Friends", clean(profile.stats?.friends)],
  ];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  info.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, 14, y);
    y += 8;
  });

  // POSTS SECTION
  doc.addPage();
  y = 20;
  drawSectionTitle("Posts");

  if (profile.posts?.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    profile.posts.forEach((post, index) => {
      if (y > 260) {
        doc.addPage();
        y = 20;
      }

      // Post Card
      doc.setDrawColor(220);
      doc.setFillColor(245, 245, 245);
      doc.rect(12, y - 3, 185, 50, 'F');

      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text(`Post ${index + 1}`, 14, y);
      y += 6;

      doc.setFont("helvetica", "normal");
      const postText = clean(post.text || "No content");
      const wrapped = doc.splitTextToSize(postText, 180);
      wrapped.forEach((line) => {
        doc.text(line, 14, y);
        y += 5;
      });

      if (post.attached_medias_url?.length) {
        post.attached_medias_url.forEach((media, i) => {
          doc.text(`Media ${i + 1}: ${clean(media)}`, 14, y);
          y += 5;
        });
      }

      y += 2;
      doc.setFont("helvetica", "italic");
      const stats = `Likes: ${clean(post.likes)}   Comments ${clean(post.comments)}   Viewed ${clean(post.views)} `;
      doc.text(stats, 14, y);
      y += 14;
    });
  } else {
    doc.setFont("helvetica", "normal");
    doc.text("No posts available.", 14, y);
  }

  // FOLLOWERS SECTION
  if (profile.followers?.length > 0) {
    doc.addPage();
    y = 20;
    drawSectionTitle("Followers");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    profile.followers.forEach((follower, idx) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${idx + 1}. Username: ${clean(follower.username)}`, 14, y);
      y += 6;
      doc.text(`   Full Name: ${clean(follower.full_name)}`, 14, y);
      y += 10;
    });
  }

  // FOLLOWING SECTION
  if (profile.following?.length > 0) {
    doc.addPage();
    y = 20;
    drawSectionTitle("Following");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    profile.following.forEach((follow, idx) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${idx + 1}. Username: ${clean(follow.username)}`, 14, y);
      y += 6;
      doc.text(`   Full Name: ${clean(follow.full_name)}`, 14, y);
      y += 10;
    });
  }

  // Save
  doc.save(`${clean(profile.username)}_profile.pdf`);
};


  

  return (
    <div id="profiles-container" className="profiles-container">
    {[...profiles]
      .sort((a, b) => new Date(b.created_time) - new Date(a.created_time))
      .map((profile, index) => (
        <div className="profile-card" key={index} style={{ position: 'relative' }}>
          {/* Download Icon - Positioned at the top-left corner */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '12px',
              fontSize: '24px',
              cursor: 'pointer',
              background: 'white',
              padding: '5px',
              borderRadius: '8px',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={() => generatePDF(profile)}
          >
            <FontAwesomeIcon icon={faDownload} />
          </div>

          <div className="p-color-strip" style={{ background: getPlatformColor(profile.platform.toLowerCase()) }}></div>
          <div className="profile-content">
          <img
  src={`/api/fetch-image/?imgSrc=${encodeURIComponent(profile.imgSrc)}`}
  alt={profile.username}
/>

            <div className="platform-icon" style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'white',
              padding: '5px',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
            }} onClick={() => generatePDF(profile)}>
              <img 
                src={getPlatformIcon(profile.platform)} 
                alt={`${profile.platform} icon`} 
                style={{ width: '100%', height: '100%', borderRadius: '50%' }} 
              />
            </div>
            <div className="username">{profile.username}</div>
            <div className="location"><i className="fa fa-map-marker"></i> {profile.location}</div>
            <div className="stats" style={{ fontWeight: 'bold' }}>
              <div className="stat">{profile.stats.posts}</div>
              <div className="stat">{profile.stats.followers}</div>
              <div className="stat">{profile.stats.friends}</div>
            </div>
            <div className="stats-title">
              <div className="stat">Posts</div>
              <div className="stat">Followers</div>
              <div className="stat">Friends</div>
            </div>
            <div className="actions">
              <button className="action-btn view-btn" onClick={() => onViewProfile(profile)}>
                <FontAwesomeIcon icon={faEye} />
                <div className="hover-text view-btn-hover">View Profile</div>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProfilingCards;
